import React, { useEffect, useState } from 'react';
import { ArrowUpRight, BookOpen } from 'lucide-react';
import { getFrameworks } from '../../utils/api';

const regionTone = {
  EU: 'bg-[#E8F3EE] text-[#1B6F5E]',
  US: 'bg-[#EEEEF7] text-[#3D3D8A]',
  California: 'bg-[#FBF1E5] text-[#A66A23]',
  Global: 'bg-[#E8EEF3] text-[#2F4858]',
  UK: 'bg-[#F2EAF2] text-[#6B3F73]',
};

export default function FrameworksPage() {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFrameworks()
      .then(setFrameworks)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalRules = frameworks.reduce((a, b) => a + (b.activeRules || 0), 0);

  return (
    <div className="max-w-[1280px]">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">
            Framework registry
          </div>
          <h2 className="mt-2 font-display text-[36px] lg:text-[44px] leading-[1.05] text-[#0B1F2D]">
            Active <span className="italic">rule packs.</span>
          </h2>
          <p className="mt-3 text-[14.5px] leading-[1.55] text-[#0B1F2D]/65 max-w-[640px]">
            All ESG-as-Code® rule packs active in the engine. Every rule maps to a regulatory clause.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-[#0B1F2D]/65">
          <BookOpen className="w-4 h-4" />
          <span className="font-mono-tight font-semibold text-[#0B1F2D]">{loading ? '—' : totalRules}</span> total rules across{' '}
          <span className="font-mono-tight font-semibold text-[#0B1F2D]">{loading ? '—' : frameworks.length}</span> frameworks
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[13px] text-[#0B1F2D]/55">Loading frameworks...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {frameworks.map((fw) => (
            <div key={fw.code} className="card-lift rounded-2xl border border-[#E8E5DC] bg-white p-7 flex flex-col">
              <div className="flex items-start justify-between">
                <span className={`text-[10.5px] font-medium px-2.5 py-1 rounded-full ${regionTone[fw.jurisdiction] || 'bg-[#E8E5DC] text-[#0B1F2D]'}`}>
                  {fw.jurisdiction}
                </span>
                <span className="font-mono-tight text-[10.5px] text-[#0B1F2D]/40">{fw.code}</span>
              </div>
              <div className="mt-5 font-display text-[24px] leading-[1.15] text-[#0B1F2D]">{fw.name}</div>
              <div className="mt-3 flex items-center gap-4 text-[12.5px] text-[#0B1F2D]/65">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1B6F5E]" />
                  {fw.activeRules} active rules
                </span>
              </div>
              <div className="mt-auto pt-5">
                <div className="h-px bg-[#E8E5DC] mb-4" />
                <div className="flex items-center justify-between text-[11.5px]">
                  <span className="text-[#0B1F2D]/55">ESG-as-Code® · Deterministic</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-[#0B1F2D]/40" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
